import pushNotificationConfig from '../configs/pushNotificationConfig';
import awsRumActionNames from '../consts/aws/awsRumActionNames';
import awsRumActionTypes from '../consts/aws/awsRumActionTypes';
import awsRumStatusTypes from '../consts/aws/awsRumStatusTypes';
import envVariables from '../consts/envVariables';
import PushSubscriptionError from '../errors/pushSubscriptionError';
import {selectLanguageCode} from '../state/selectors/session';
import {setIsPushNotificationPageVisible, setIsSubscriptionEnabled} from '../state/slice/session';
import {dispatch, getState} from '../state/store';
import uaUtils from '../utils/uaUtils';
import damSubscriptionService from './dam/damSubscriptionService';
import rumCustomEvents from './events/rumCustomEvents';
import log from './logger/log';
import networkStatusService from './networkStatusService';
import serviceWorkerService from './serviceWorkerService';
import storageService from './storageService';

const urlB64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
};

const getNotificationPermission = async () => {
    const storagePermission = storageService.getSubscriptionPermissionFromStorage();
    const permission = 'Notification' in window && Notification.permission;

    log.debug(`pushNotificationService: Notification permission: ${permission}`);

    if (!uaUtils.getIsSafari()) return permission;
    // Handle Safari notification permission bugs

    log.debug(`pushNotificationService: Previously Safari notification permission: ${storagePermission}`);

    if (permission === 'default' && storagePermission === 'denied') {
        // Safari has notification permission bugs when a user denies notifications.
        // If app is opened in a new tab or as a PWA, the permission is reset to 'default' until the user calls requestPermission()
        // In Safari, Notification.requestPermission() can only be triggered by a user gesture.

        return 'denied';
    }

    if (permission === 'default' && storagePermission === 'granted') {
        // When a user disables push notifications in Safari settings,
        // Notification.permission reverts to "default" and getSubscription() still returns a valid subscription.
        const swReg = await serviceWorkerService.registerServiceWorker();
        const subscription = await swReg?.pushManager.getSubscription();

        if (subscription) {
            log.debug('pushNotificationService: Safari subscription exists while permission is default');

            return 'denied';
        }
    }

    return permission;
};

const getIsPermissionGranted = async () => (await getNotificationPermission()) === 'granted';

const getIsPermissionDenied = async () => (await getNotificationPermission()) === 'denied';

const askPermission = async () => {
    if (await getIsPermissionGranted()) {
        storageService.setSubscriptionPermissionToStorage('granted');
        return;
    }

    const permission = await Notification.requestPermission();

    storageService.setSubscriptionPermissionToStorage(permission);

    if (permission !== 'granted') {
        throw new Error('Notifications are denied.');
    }
};

const sendSubscription = async (subscription) => {
    try {
        const languageCode = selectLanguageCode(getState());
        const pushNotificationTypes = getPushNotificationTypes();

        await damSubscriptionService.postUserSubscriptionInfo(
            subscriptionMapping(subscription),
            languageCode,
            pushNotificationTypes
        );

        storageService.setSubscriptionLanguageCodeToStorage(languageCode);
        storageService.setSubscriptionNotificationTypesToStorage(pushNotificationTypes);
    } catch (e) {
        // Clear the existing subscription, will be retried when reopened
        await subscription.unsubscribe();

        throw e;
    }
};

const subscribeWithCurrentKey = async (swReg) => {
    const publicKey = envVariables.WEB_PUSH_PUBLIC_KEY;

    const subscription = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
    });

    // Browsers that do not expose subscription.options.applicationServerKey rely on the stored key
    storageService.setSubscriptionPublicKeyToStorage(publicKey);

    return subscription;
};

const getSubscription = async (swReg) => {
    let subscription = await getExistingSubscription();

    if (!subscription) {
        subscription = await subscribeWithCurrentKey(swReg);
    }

    log.debug(`pushNotificationService: Successfully retrieved subscription: ${JSON.stringify(subscription)}`);

    await sendSubscription(subscription);
};

const getIsSubscriptionKeyOutdated = (subscription) => {
    const publicKey = envVariables.WEB_PUSH_PUBLIC_KEY;

    if (!publicKey) return false;

    const subscriptionKey = subscription.options?.applicationServerKey;

    if (subscriptionKey) {
        const expectedKey = urlB64ToUint8Array(publicKey);
        const actualKey = new Uint8Array(subscriptionKey);

        return actualKey.length !== expectedKey.length || actualKey.some((byte, index) => byte !== expectedKey[index]);
    }

    return storageService.getSubscriptionPublicKeyFromStorage() !== publicKey;
};

let isRefreshingSubscription = false;

// A push subscription is bound to the VAPID public key it was created with. After a key rotation
// the existing subscription stops receiving pushes and must be replaced by one created with the new key.
const refreshOutdatedSubscription = async (subscription) => {
    if (isRefreshingSubscription) return;

    isRefreshingSubscription = true;

    try {
        if (!(await getIsPermissionGranted())) return;

        const swReg = await serviceWorkerService.registerServiceWorker();

        if (!swReg) return;

        log.info('pushNotificationService: Subscription uses an outdated VAPID key, resubscribing');

        try {
            await damSubscriptionService.delUserSubscriptionInfo(subscription.endpoint);
        } catch (e) {
            log.warn('pushNotificationService: Failed to delete the outdated subscription', e.message);
        }

        await subscription.unsubscribe();

        dispatch(setIsSubscriptionEnabled(false));

        const newSubscription = await subscribeWithCurrentKey(swReg);

        await sendSubscription(newSubscription);

        dispatch(setIsSubscriptionEnabled(true));

        log.info('pushNotificationService: Successfully resubscribed with the current VAPID key');
    } catch (e) {
        log.error('pushNotificationService: Failed to resubscribe with the current VAPID key', e.message);
    } finally {
        isRefreshingSubscription = false;
    }
};

const subscribe = async () => {
    const swReg = await serviceWorkerService.registerServiceWorker();

    if (!swReg) return;

    try {
        await askPermission();

        networkStatusService.checkIsOnline();

        await getSubscription(swReg);

        dispatch(setIsSubscriptionEnabled(true));

        rumCustomEvents.pushPushNotification({
            action: awsRumActionTypes.SUBSCRIBE,
            status: awsRumStatusTypes.SUCCESS,
        });

        log.debug('pushNotificationService: Successfully subscribed to push notifications');
    } catch (e) {
        dispatch(setIsSubscriptionEnabled(false));

        rumCustomEvents.pushPushNotification({
            action: awsRumActionTypes.SUBSCRIBE,
            status: awsRumStatusTypes.FAILED,
        });

        log.error('pushNotificationService: Failed to subscribe to push notifications', e.message);
    } finally {
        await updatePushNotificationPermission(true);
    }
};

const getExistingSubscription = async () => {
    const swReg = await serviceWorkerService.registerServiceWorker();

    if (!swReg) return;

    try {
        const subscription = await swReg.pushManager.getSubscription();

        if (!subscription) {
            throw new PushSubscriptionError('No existing subscription found');
        }

        log.debug(
            `pushNotificationService: Successfully received an existing subscription: ${JSON.stringify(subscription)}`
        );

        dispatch(setIsSubscriptionEnabled(true));

        return subscription;
    } catch (e) {
        log.error('pushNotificationService: Failed to receive an existing subscription', e.message);

        dispatch(setIsSubscriptionEnabled(false));
    }
};

const sendUnsubscribedEvent = async () => {
    if (storageService.getIsSubscriptionUnsubscribed()) return;

    if (await getIsPermissionGranted()) return;

    const storagePermission = storageService.getSubscriptionPermissionFromStorage();

    if (storagePermission === 'granted') {
        storageService.setIsSubscriptionUnsubscribed();

        rumCustomEvents.pushPushNotification({
            action: awsRumActionTypes.SUBSCRIBE,
            rumActionName: awsRumActionNames.CHECK,
            status: awsRumStatusTypes.FAILED,
        });
    }
};

const unsubscribe = async () => {
    const subscription = await getExistingSubscription();

    if (subscription) {
        try {
            await subscription.unsubscribe();
            await damSubscriptionService.delUserSubscriptionInfo(subscription.endpoint);

            storageService.removeSubscriptionLanguageCodeFromStorage();
            storageService.removeSubscriptionNotificationTypesFromStorage();
            storageService.removeSubscriptionPublicKeyFromStorage();
            serviceWorkerService.postMessageRemoveNotificationsAndBadges();
            dispatch(setIsSubscriptionEnabled(false));

            log.info(`pushNotificationService: Successfully unsubscribed from push notifications`);
        } catch (e) {
            log.error('pushNotificationService: Failed to unsubscribe from push notifications', e.message);
        }
    }
};

const getIsPushNotificationsSupported = async () => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    if (!isSupported) return false;

    const swReg = await serviceWorkerService.registerServiceWorker();

    return !!swReg;
};

const subscriptionMapping = (subscription) => {
    // parse and stringify is required to extract all data from PushSubscription
    const data = JSON.parse(JSON.stringify(subscription));

    return {
        endpoint: data?.endpoint,
        expirationTime: data?.expirationTime || null, // expirationTime is not supported in Safari
        keys: {
            auth: data?.keys?.auth,
            p256dh: data?.keys?.p256dh,
        },
    };
};

const checkPushNotificationPageVisibility = async (isBackgroundSubscriptionDisabled) => {
    if (!(await getIsPushNotificationsSupported())) {
        log.info('pushNotificationService: Push notifications are not supported in the browser');
        return false;
    }

    await sendUnsubscribedEvent();

    if (await getIsPermissionDenied()) {
        log.info('pushNotificationService: User denied push notifications previously');
        return false;
    }

    storageService.removeIsSubscriptionUnsubscribed();

    const subscription = await getExistingSubscription();

    if (subscription) {
        log.info('pushNotificationService: Push subscription already exists');

        if (getIsSubscriptionKeyOutdated(subscription)) {
            refreshOutdatedSubscription(subscription);
        } else {
            syncSubscriptionNotificationTypes(subscription);
        }

        return false;
    }

    if (await getIsPermissionGranted()) {
        if (!isBackgroundSubscriptionDisabled) {
            log.info('pushNotificationService: Permission granted, subscribe in background');
            subscribe();
        }

        return false;
    }

    return true;
};

const updatePushNotificationPermission = async (isBackgroundSubscriptionDisabled) => {
    const isVisible = await checkPushNotificationPageVisibility(isBackgroundSubscriptionDisabled);

    log.debug(`pushNotificationService: Push notification page visibility updated, isVisible: ${isVisible}`);

    dispatch(setIsPushNotificationPageVisible(isVisible));
};

const getPushNotificationTypes = () => {
    const disabledSubTypesMap = storageService.getDisabledNotificationTypes();

    return Object.fromEntries(
        Object.entries(pushNotificationConfig).map(([type, subTypes]) => {
            if (!Array.isArray(subTypes)) return [type, subTypes];

            const disabledSubTypes = disabledSubTypesMap?.[type] || [];

            const enabledSubTypes = subTypes.filter((subType) => !disabledSubTypes.includes(subType));

            return [type, enabledSubTypes];
        })
    );
};

const updateSubscriptionLocalization = async (languageCode) => {
    const subscription = await getExistingSubscription();

    if (!subscription) return;

    const storedLanguageCode = storageService.getSubscriptionLanguageCodeFromStorage();

    if (storedLanguageCode === languageCode) return;

    const pushNotificationTypes = getPushNotificationTypes();

    try {
        await damSubscriptionService.putUserSubscriptionInfo(
            subscription.endpoint,
            languageCode,
            pushNotificationTypes
        );
        storageService.setSubscriptionLanguageCodeToStorage(languageCode);
        storageService.setSubscriptionNotificationTypesToStorage(pushNotificationTypes);
    } catch (e) {
        log.error('pushNotificationService: Failed to update subscription', e.message);
    }
};

const syncSubscriptionNotificationTypes = async (subscription) => {
    const pushNotificationTypes = getPushNotificationTypes();
    const sentNotificationTypes = storageService.getSubscriptionNotificationTypesFromStorage();

    if (JSON.stringify(sentNotificationTypes) === JSON.stringify(pushNotificationTypes)) return;

    const languageCode = storageService.getSubscriptionLanguageCodeFromStorage() || selectLanguageCode(getState());

    try {
        await damSubscriptionService.putUserSubscriptionInfo(
            subscription.endpoint,
            languageCode,
            pushNotificationTypes
        );
        storageService.setSubscriptionNotificationTypesToStorage(pushNotificationTypes);

        log.info('pushNotificationService: Successfully synchronized subscription notification types');
    } catch (e) {
        log.error('pushNotificationService: Failed to synchronize subscription notification types', e.message);
    }
};

const updateSubscriptionNotificationTypes = async () => {
    const subscription = await getExistingSubscription();

    if (!subscription) {
        throw new PushSubscriptionError('No existing subscription found to update notification types');
    }

    const storedLanguageCode = storageService.getSubscriptionLanguageCodeFromStorage();
    const pushNotificationTypes = getPushNotificationTypes();

    await damSubscriptionService.putUserSubscriptionInfo(
        subscription.endpoint,
        storedLanguageCode,
        pushNotificationTypes
    );
    storageService.setSubscriptionNotificationTypesToStorage(pushNotificationTypes);
};

export default {
    subscribe,
    unsubscribe,
    getExistingSubscription,
    updateSubscriptionLocalization,
    updatePushNotificationPermission,
    updateSubscriptionNotificationTypes,
};
